import { z } from "zod";

/*
  NINJA RANKS. Edit these to make your own levels. Keep them in order from
  lowest to highest. Each rank after the first is earned by passing its Dojo
  Trial (see trials.ts).

  score:   the Phase 1 score threshold, from when a best score set the rank.
           Kept so old saves migrate to the same rank; don't change it.
  art:     which ninja drawing to use
  suit / band: suit colour and headband/belt colour
  eyes:    happy, normal, fierce or glow
  colour:  card border and title colour
  powers:  Speed, Stealth, Power, Brain (out of 10)
  image:   optional data: URI to replace the drawing with your own picture
*/

export const ART_KEYS = [
  "seedling",
  "bamboo",
  "cub",
  "silent",
  "star",
  "storm",
  "fire",
  "dragon",
  "legend",
] as const;
export type ArtKey = (typeof ART_KEYS)[number];

export const EYE_STYLES = ["happy", "normal", "fierce", "glow"] as const;
export type EyeStyle = (typeof EYE_STYLES)[number];

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected a #rrggbb colour");

export const RankSchema = z.object({
  name: z.string().min(1),
  score: z.number().int().nonnegative(),
  art: z.enum(ART_KEYS),
  suit: hex,
  band: hex,
  eyes: z.enum(EYE_STYLES),
  colour: hex,
  motto: z.string(),
  move: z.string(),
  powers: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  image: z.string().startsWith("data:").optional(),
});
export type Rank = z.infer<typeof RankSchema>;

export const RanksSchema = z
  .array(RankSchema)
  .min(1)
  .refine((ranks) => ranks[0]?.score === 0, "the first rank must need a score of 0")
  .refine(
    (ranks) => ranks.every((r, i) => i === 0 || r.score > (ranks[i - 1]?.score ?? -1)),
    "rank scores must strictly increase",
  );

export const POWER_NAMES = ["Speed", "Stealth", "Power", "Brain"] as const;

export const RANKS: readonly Rank[] = RanksSchema.parse([
  {
    name: "Seedling",
    score: 0,
    art: "seedling",
    suit: "#6BB14A",
    band: "#F5B82E",
    eyes: "happy",
    colour: "#7DB83A",
    motto: "Every ninja starts as a tiny seed.",
    move: "Leaf Leap",
    powers: [2, 2, 1, 2],
  },
  {
    name: "Bamboo Sprout",
    score: 10,
    art: "bamboo",
    suit: "#2F8F5B",
    band: "#E4572E",
    eyes: "normal",
    colour: "#2F8F5B",
    motto: "Bends in the wind but never breaks.",
    move: "Bamboo Spin",
    powers: [3, 3, 3, 3],
  },
  {
    name: "Shadow Cub",
    score: 18,
    art: "cub",
    suit: "#5E4FA2",
    band: "#F48FB8",
    eyes: "normal",
    colour: "#6B5CA5",
    motto: "Quiet paws, quick answers.",
    move: "Pounce Strike",
    powers: [4, 5, 3, 4],
  },
  {
    name: "Silent Step",
    score: 26,
    art: "silent",
    suit: "#2E5E8C",
    band: "#8FD0E8",
    eyes: "normal",
    colour: "#3E7CB1",
    motto: "You'll never hear this ninja coming.",
    move: "Smoke Vanish",
    powers: [5, 7, 4, 5],
  },
  {
    name: "Swift Star",
    score: 34,
    art: "star",
    suit: "#2A3566",
    band: "#F5B82E",
    eyes: "fierce",
    colour: "#E0A10B",
    motto: "Every throw hits the target.",
    move: "Star Storm",
    powers: [7, 6, 5, 6],
  },
  {
    name: "Storm Ninja",
    score: 42,
    art: "storm",
    suit: "#1F7F95",
    band: "#FFD23F",
    eyes: "fierce",
    colour: "#2A9DB5",
    motto: "Faster than a lightning bolt.",
    move: "Thunder Dash",
    powers: [8, 6, 7, 7],
  },
  {
    name: "Fire Blade",
    score: 52,
    art: "fire",
    suit: "#B8322A",
    band: "#1D2340",
    eyes: "fierce",
    colour: "#D9483B",
    motto: "Burns through every question.",
    move: "Blazing Slash",
    powers: [8, 7, 9, 8],
  },
  {
    name: "Dragon Master",
    score: 64,
    art: "dragon",
    suit: "#177A5A",
    band: "#F5B82E",
    eyes: "fierce",
    colour: "#1F9E77",
    motto: "Tamed the dragon with a single sum.",
    move: "Dragon's Roar",
    powers: [9, 8, 9, 9],
  },
  {
    name: "Legend",
    score: 80,
    art: "legend",
    suit: "#15182B",
    band: "#F5B82E",
    eyes: "glow",
    colour: "#E0A10B",
    motto: "The greatest ninja who ever lived.",
    move: "Infinity Strike",
    powers: [10, 10, 10, 10],
  },
]);

/**
 * The lead: how many ranks a strand may run ahead of the lowest other strand
 * that's switched on. The trial for rank N in a strand is locked until every
 * other strand holds at least N minus the lead (Mix is left out both ways).
 */
export const STRAND_LEAD = 2;
